<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <!-- 功能代码 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        功能代码 <span class="text-red-500">*</span>
      </label>
      <input
        v-model="formData.featureCode"
        type="text"
        required
        :disabled="isEdit"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 font-mono"
        placeholder="例如: speaking.ai_answer"
      />
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">格式：category.feature_name</p>
    </div>

    <!-- 功能名称 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        功能名称 <span class="text-red-500">*</span>
      </label>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          v-model="formData.name.zh"
          type="text"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
          placeholder="中文，例如: AI 口语答案生成"
        />
        <input
          v-model="formData.name.en"
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
          placeholder="English, e.g. AI Speaking Answer Generation"
        />
      </div>
    </div>

    <!-- 分类和应用 -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          功能分类 <span class="text-red-500">*</span>
        </label>
        <select
          v-model="formData.category"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
        >
          <option value="" disabled>请选择分类</option>
          <option v-for="option in categoryOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          所属应用 <span class="text-red-500">*</span>
        </label>
        <select
          v-model="formData.appName"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
        >
          <option value="" disabled>请选择应用</option>
          <option v-for="option in appNameOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
    </div>

    <!-- 默认配额类型 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        默认配额类型 <span class="text-red-500">*</span>
      </label>
      <select
        v-model="formData.defaultQuotaType"
        required
        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
      >
        <option value="" disabled>请选择配额类型</option>
        <option v-for="option in quotaTypeOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        配额类型说明：布尔型（开关）、每日配额、每月配额、每年配额、无限制
      </p>
    </div>

    <!-- 功能描述 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">功能描述</label>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <textarea
          v-model="formData.description.zh"
          rows="3"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
          placeholder="中文描述"
        />
        <textarea
          v-model="formData.description.en"
          rows="3"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
          placeholder="English description"
        />
      </div>
    </div>

    <!-- 按钮 -->
    <div class="flex justify-end gap-3 pt-4">
      <button
        type="button"
        @click="handleCancel"
        class="cancel-btn"
      >
        取消
      </button>
      <button
        type="submit"
        class="primary-btn"
      >
        {{ isEdit ? '更新' : '创建' }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  getFeatureCategoryOptions,
  getAppNameOptions,
  getQuotaTypeOptions,
  FeatureCategory,
  AppName,
  QuotaType,
} from '@/types/membership/enums'
import type { Feature, CreateFeatureRequest, UpdateFeatureRequest } from '@/types/membership/feature'
import { emptyLocalizedText, normalizeLocalizedText, type LocalizedText } from '@/types/membership/localized'

type FeatureFormData = Omit<CreateFeatureRequest, 'description'> & {
  description: LocalizedText
}

/**
 * Props
 */
interface Props {
  feature?: Feature
  isEdit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isEdit: false,
})

/**
 * Emits
 */
const emit = defineEmits<{
  submit: [data: CreateFeatureRequest | UpdateFeatureRequest]
  cancel: []
}>()

/**
 * 表单数据
 */
const formData = ref<FeatureFormData>({
  featureCode: '',
  name: emptyLocalizedText(),
  category: '' as FeatureCategory,
  appName: '' as AppName,
  defaultQuotaType: '' as QuotaType,
  description: emptyLocalizedText(),
})

/**
 * 选项列表
 */
const categoryOptions = getFeatureCategoryOptions()
const appNameOptions = getAppNameOptions()
const quotaTypeOptions = getQuotaTypeOptions()

/**
 * 监听 feature prop 变化，初始化表单
 */
watch(
  () => props.feature,
  (newFeature) => {
    if (newFeature) {
      formData.value = {
        featureCode: newFeature.featureCode,
        name: normalizeLocalizedText(newFeature.name),
        category: newFeature.category,
        appName: newFeature.appName,
        defaultQuotaType: newFeature.defaultQuotaType,
        description: normalizeLocalizedText(newFeature.description),
      }
    }
  },
  { immediate: true }
)

/**
 * 处理提交
 */
const handleSubmit = () => {
  emit('submit', formData.value)
}

/**
 * 处理取消
 */
const handleCancel = () => {
  emit('cancel')
}
</script>
